import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useReferralsContext } from '@user/components/Referrals/context'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { selectAllEmployees } from '@user/store/employee/slice'
import { selectAllUsers } from '@user/store/users'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

export type EmployeeUserOption = {
  id: string
  emailAddress: string
  firstName: string
  lastName: string
  value: string
  userName: string | null
}

export function useData() {
  const [{ referralEmployeeValue }] = useReferralsContext()
  const employees = useSelector(selectAllEmployees)
  const users = useSelector(selectAllUsers)
  const tidalThemeActive = useTidalThemeActive()

  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  const options: EmployeeUserOption[] = useMemo(() => {
    const employeeOptions = employees
      .filter((employee) => employee.firstName.length > 0)
      .map((employee) => ({
        id: employee.id,
        emailAddress: employee.email,
        firstName: employee.firstName,
        lastName: employee.lastName,
        value: `${employee.firstName} ${employee.lastName}`,
        userName: employee.username,
      }))

    const userOptions = users
      .filter(
        (user) => user.firstName.length > 0 && user.externalUserId === null,
      )
      .map((user) => ({
        id: user.id,
        emailAddress: user.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        value: `${user.firstName} ${user.lastName}`,
        userName: null,
      }))

    const combinedOptions = employeeOptions
      .concat(userOptions)
      .sort((a, b) => a.firstName.localeCompare(b.firstName))

    return combinedOptions
  }, [employees, users])

  const getOptionLabel = useCallback(
    (option: EmployeeUserOption) =>
      `${option.firstName} ${option.lastName} (${option.emailAddress})`,
    [],
  )

  const getId = useCallback((option: EmployeeUserOption) => {
    return String(option.id)
  }, [])

  const employeeGroupBy = useCallback((option) => option.label, [])

  const initialOption = useMemo(() => {
    if (referralEmployeeValue && referralEmployeeValue.length > 0) {
      const value = options.find((x) => x.value === referralEmployeeValue)
      if (value) return value
    }
    return null
  }, [options, referralEmployeeValue])

  return {
    options,
    employeeGroupBy,
    getOptionLabel,
    getId,
    initialOption,
    referralEmployeeValue,
    density,
  } as const
}
